import React, { useEffect, useMemo, useState } from 'react';
import { Table, Input, Button, Dropdown, Menu, Checkbox, Steps, Space, Flex, Tag, TableColumnProps, Select, MenuProps, FormInstance, notification, Typography } from 'antd';
import ReusableTable from '../../../../components/TableComponent';
import { FormColumnConfig, MasterConfigs } from '../../../../interfaces/entity';
import { constants } from '../../../../constants/common';
import moment from 'moment';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import { useSelector } from 'react-redux';
import { CheckCircleOutlined, CheckOutlined, DownOutlined, FilterOutlined } from '@ant-design/icons'
import Modal from 'antd/es/modal/Modal';
import FormSetup from '../../../../components/Forms/FormSetup';
import dayjs from 'dayjs';
import RenderForm from '../../../../components/Forms/Form';
import { useDispatch } from 'react-redux';
import { postData } from '../../../../actions/developer';
import LOPReversal from './LOPReversal';
const { Text } = Typography;

const LeaveAppliedComponent = ({ onCloseModel }) => {
    const [currentStep, setCurrentStep] = React.useState(0);
    const params = useSelector(({ app }) => app?.params)
    const currentMonth = moment(params?.selectedMonth || moment().format('MMM YYYY'), 'MMM YYYY').format('MM-YYYY')
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [deductLeave, setDeductleave] = useState(false)
    const [form, setForm] = useState<FormInstance>(null)
    const [selectedUser, setSelectedUser] = useState(null)
    const [selectedDays, setSelectedDays] = useState([])
    const [manageLOP, setManageLOP] = useState(false)
    const [currentRecord, setCurrentRecord] = useState(null)

    const dispatch = useDispatch()

    const attendanceOptions = ['Present', 'Absent', 'On Leave', 'LOP Suggested', 'LOP Confirmed', 'Weekend', 'Holiday'];

    const handleCheckboxChange = (checked: boolean, label: string) => {
        const getValue = (prev) => {
            const exists = prev.includes(label);
            if (checked && !exists) return [...prev, label];
            if (!checked && exists) return prev.filter((item) => item !== label);
            return prev;
        }
        const value = getValue(selectedFilters)
        setSelectedFilters(value);
    };

    const config: MasterConfigs = useMemo(() => ({
        api: () => '/run-payroll/leave-and-no-attendance',
        queryParams: {
            month: currentMonth
        },
        tableProps: {
            rowKey: '_id',
            expandable: {
                expandedRowRender: (record) => {
                    return <div>
                        {currentStep == 0 && <div className='flex justify-end gap-2'>
                            <Button type='primary' onClick={() => {
                                setDeductleave(true);
                                setSelectedUser(record?._id);
                            }} disabled={selectedDays.length == 0}>Deduct Leave</Button>
                            <Dropdown
                                trigger={['click']}
                                dropdownRender={() => (
                                    <div className="p-3 bg-white rounded-sm" onClick={(e) => e.stopPropagation()}>
                                        {attendanceOptions.map((label) => (
                                            <div key={label} className="mb-2">
                                                <Checkbox
                                                    name={label}
                                                    onChange={(e) => handleCheckboxChange(e.target.checked, label)}
                                                >
                                                    {label}
                                                </Checkbox>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            >
                                <Button>
                                    <div className='flex justify-between items-center gap-1'>
                                        <FilterOutlined />
                                        Filter
                                        <DownOutlined />
                                    </div>
                                </Button>
                            </Dropdown>
                        </div>}
                        {currentStep == 1 && <div className='flex justify-end gap-2'>
                            <Button type='primary' onClick={() => {
                                setManageLOP(true);
                                setSelectedUser(record?._id);
                                setCurrentRecord(record)
                            }} disabled={selectedDays.length == 0}>Manage LOP</Button>
                        </div>}
                        <Table
                            className='w-full mt-2'
                            rowKey={'_id'}
                            rowSelection={{
                                type: 'checkbox',
                                selections: false,
                                onChange: (selectedRowKeys, selectedRows) => {
                                    setSelectedDays(selectedRows.map(ele => ele.date))
                                },
                                getCheckboxProps: (record: any) => ({
                                    disabled: record?.dayStatus == 'weekend', // Column configuration not to be checked
                                })
                            }}
                            columns={getAttendanceColumns()}
                            dataSource={
                                record.attendanceLogs.filter(ele => (currentStep == 0) ||
                                    (currentStep == 1 && (ele?.leaveData?.leaveDetail?.leaveCategoryDetail?.isPaidLeave == false || ele?.dayStatus == 'absent'))).map((log) => ({
                                        key: log._id,
                                        ...log,
                                    }))
                            }
                            scroll={{ y: 300 }
                            }
                            pagination={{
                                pageSize: 7
                            }}
                            rowClassName={(record) =>
                                record.dayStatus === "absent" || record.totalHours < 1 && record.dayStatus == 'present'
                                    ? "bg-red-50"
                                    : ""
                            }
                        />
                    </div>
                },
                rowExpandable: (record) => true,
            }
        },
        getColumns: () => employeeColumns as TableColumnProps[],
    }), [params?.selectedMonth, currentStep, selectedFilters, selectedDays])

    const getAttendanceColumns = (): TableColumnProps[] => [
        {
            title: "Date",
            dataIndex: "date",
            width: 200,
            fixed: 'left',
            render: (date) => moment(date).format("dddd DD MMM, YYYY"),
        },
        {
            title: 'Clock In',
            dataIndex: 'startDate',
            key: 'startDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchStatus == 'on-time') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.startDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + (record?.punchStatus == 'on-time' ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchStatus}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: 'Clock Out',
            dataIndex: 'endDate',
            key: 'endDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchOut == 'on-time' || record.punchOut == 'late') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.endDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + ((record?.punchOut == 'on-time' || record?.punchOut == 'late') ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchOut}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: "Working Hours",
            dataIndex: "totalHours",
            width: 150,
            align: 'center',
            render: (hours, record) => record?.dayStatus == 'present' ? <>
                {
                    hours <= 5 ? (
                        <span className='text-red-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) : hours > 5 && hours <= 7 ? (
                        <span className='text-red-800 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) :
                        <span className='text-green-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                }
            </> : '-',
        },
        {
            title: "Day Status",
            dataIndex: "dayStatus",
            width: 150,
            align: 'center',
            render: (status) => {
                const colorMap: Record<string, string> = {
                    present: "green",
                    absent: "red",
                    weekend: "blue",
                    leave: "orange",
                };
                return <Tag bordered={false} color={colorMap[status] || "default"}>{status.toUpperCase()}</Tag>
            },
        },
        {
            title: "Leave Type",
            width: 150,
            render: (_, record) =>
                record.leaveData?.leaveDetail?.leaveTypeDetail?.leaveName || "-",
        },
        {
            title: "Leave Status",
            width: 150,
            align: 'center',
            render: (_, record) =>
                record.leaveData?.leaveStatus ? (
                    <Tag color="green">{record.leaveData.leaveStatus}</Tag>
                ) : (
                    "-"
                ),
        },
        {
            title: "LOP (Suggested)",
            width: 150,
            align: 'center',
            onCell: (record) => {
                return {
                    style: {
                        backgroundColor: '#ffffff',
                    },
                };
            },
            render: (_, record) => (record?.leaveData?.leaveDetail?.leaveCategoryDetail?.isPaidLeave == false || record?.dayStatus == 'absent')
                ? <CheckOutlined className='text-green-700' /> : ''
        },
        currentStep == 1 ? {
            title: "LOP comment",
            width: 150,
            align: 'center',
            ellipse: true,
            onCell: (record) => {
                return {
                    style: {
                        backgroundColor: '#ffffff',
                    },
                };
            },
            render: (_, record) => record?.lopData?.comment
        } : null,
        currentStep == 1 ? {
            title: "LOP (Confirmed)",
            width: 150,
            align: 'center',
            onCell: (record) => {
                return {
                    style: {
                        backgroundColor: '#ffffff',
                    },
                };
            },
            render: (_, record) => (record?.lopData && !record?.lopData?.isReversed) ? <CheckOutlined /> : ''
        } : null
    ].filter(Boolean) as TableColumnProps[];

    const lopColumns = [
        {
            title: "LOP (Confirmed)",
            width: 150,
            align: 'center',
            render: (_, record) => <Text className="font-bold">{record?.lopSummary?.totalLopCount}</Text>
        }
    ]

    const lopReversedColumns = [
        {
            title: "LOP (Confirmed)",
            width: 150,
            align: 'center',
            render: (_, record) => <Text className="font-bold">{record?.lopSummary?.totalLopCount}</Text>
        },
        {
            title: "LOP (Reversed)",
            width: 150,
            align: 'center',
            render: (_, record) => record?.reversed
        }
    ]

    const columns = [
        {
            title: "Employee",
            align: 'left',
            render: (_, record) => <SmallProfileCard
                name={constants.GET_FULL_NAME(record)}
                avatar={record?.avatar}
                subTitle={record?.employeeId}
            />
        },
        {
            title: "Shift",
            align: 'center',
            dataIndex: "shiftName",
        },
        {
            title: "Working Days",
            align: 'center',
            dataIndex: "workingDays",
        },
        {
            title: "Present Days",
            align: 'center',
            dataIndex: "presentDays",
        },
        {
            title: "Absent Days",
            align: 'center',
            dataIndex: "absentDays",
        },
        {
            title: "Total Leaves",
            align: 'center',
            dataIndex: "totalLeaves",
        },
        {
            title: "Unpaid Leaves",
            align: 'center',
            dataIndex: "unpaidLeaves",
        },
        {
            title: "LOP (Suggested)",
            width: 150,
            align: 'center',
            render: (_, record) => (record?.unpaidLeaves | 0) + (record?.absentDays | 0) || '-'
        },
    ]

    const employeeColumns = currentStep == 1
        ? [...columns, ...lopColumns]
        : currentStep == 2 ? [...columns, ...lopReversedColumns] : columns;

    const configs = {
        0: config,
        1: config,
        2: config,
    }

    const applyLeaveConfig: MasterConfigs = {
        api: () => "/leaves/user/apply",
        modalTitle: () => "Apply Leave",
        getFormColumns: () => [
            {
                type: "select",
                label: "Leave Type",
                name: "leaveType",
                isCombo: true,
                span: 16,
                rules: [{ required: true, message: "Please select a leave type" }],
            },
            {
                type: "textarea",
                label: "Reason",
                name: "reason",
                span: 16
            },
        ],
        onSubmit: async (id, values) => {
            console.log(id, values)
            return await submitLeaves(values)
        },
        onRefresh: () => {
            form.resetFields()
            setSelectedUser(null)
            setSelectedDays([])
            setDeductleave(false)
        },
        combo: [
            {
                name: "leaveType",
                api: `/leaves/user/leaves/list/?userId=${selectedUser}`,
                idKey: "_id",
                render: (e) => `${e?.leaveType?.leaveName} - (${e?.available})`,
            }
        ]
    }

    const manageLOPConfig: MasterConfigs = {
        api: () => "/",
        modalTitle: () => "Manage LOP",
        getFormColumns: () => [
            {
                type: "select",
                label: "LOP Type",
                name: "lopType",
                children: [
                    { name: 'Add as LOP', value: 'addLOP' },
                    { name: 'Remove as LOP', value: 'removeLOP' },
                ],
                span: 16,
                rules: [{ required: true, message: "Please select a LOP Type" }],
            },
            {
                type: "textarea",
                label: "Reason",
                name: "reason",
                rules: [{ required: true, message: "Please enter lop reason" }],
                span: 16
            },
        ],
        onSubmit: async (id, values) => {
            return await submitLOP(values)
        },
        onRefresh: () => {
            form.resetFields()
            setSelectedUser(null)
            setSelectedDays([])
            setManageLOP(false)
        }
    }

    const submitLeaves = async (data) => {
        try {
            if (!selectedDays || selectedDays.length == 0) {
                notification.error({ message: 'Validation Faied', description: 'Select atleast one record to proceed' })
                return { code: 300 };
            }

            if (!selectedUser) {
                notification.error({ message: 'Validation Faied', description: 'User did not selected please select a user' })
                return { code: 300 };
            }

            const payload = {
                dates: selectedDays,
                user: selectedUser,
                leaveType: data?.leaveType,
                reason: data?.reason
            }
            const resData = await dispatch(postData('/run-payroll/deduct-leave', payload))
            return resData
        } catch (error) {
            return { code: 300, message: 'Failed' }
        }
    }

    const submitLOP = async (data) => {
        try {
            if (!selectedDays || selectedDays.length == 0) {
                notification.error({ message: 'Validation Faied', description: 'Select atleast one record to proceed' })
                return { code: 300 };
            }

            if (!selectedUser) {
                notification.error({ message: 'Validation Faied', description: 'User did not selected please select a user' })
                return { code: 300 };
            }

            const payload = {
                dates: selectedDays,
                user: selectedUser,
                lopType: data?.lopType,
                reason: data?.reason,
                month: currentMonth,
                totalWorkingDays: currentRecord?.workingDays,
                presentDays: currentRecord?.presentDays,
                absentDays: currentRecord?.absentDays,
                paidLeavs: currentRecord?.totalLeaves - currentRecord?.unpaidLeaves,
                unpaidLeaves: currentRecord?.unpaidLeaves,
            }
            const resData = await dispatch(postData('/run-payroll/manage-lop', payload))
            return resData
        } catch (error) {
            return { code: 300, message: 'Failed' }
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Modal
                open={deductLeave}
                onCancel={() => setDeductleave(false)}
                title={"Deduct Leave"}
                onOk={() => form && form?.submit()}
                maskClosable={false}
            >
                <RenderForm config={applyLeaveConfig} formInstance={setForm} />
            </Modal>

            <Modal
                open={manageLOP}
                onCancel={() => setManageLOP(false)}
                title={"Manage LOP"}
                onOk={() => form && form?.submit()}
                maskClosable={false}
            >
                <RenderForm config={manageLOPConfig} formInstance={setForm} />
            </Modal>

            <div style={{ flex: '0 0 auto' }} className='bg-white shadow-md p-4  w-full'>
                <Flex align='center' gap={16}>
                    <div className='w-3/4 flex items-center gap-4'>
                        <Steps
                            current={currentStep}
                            size='small'
                            onChange={(e) => setCurrentStep(e)}
                            items={[
                                { title: 'Attendance and Leaves' },
                                { title: 'LOP SUMMARY' },
                                { title: 'LOP REVERSAL' }
                            ]}
                        />
                    </div>
                    <div className='w-1/4 flex justify-end'>
                        <Button type="primary" onClick={() => currentStep < 2 ? setCurrentStep(currentStep + 1) : onCloseModel && onCloseModel()}>Save & Continue</Button>
                    </div>
                </Flex>
            </div>

            <div
                style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    padding: '1.5rem',
                    backgroundColor: '#f9fafb' // same as bg-gray-50
                }}
            >
                {/* <div className="bg-purple-100 text-purple-800 p-4 rounded mb-4 text-sm font-medium">
                    All leave (approved or pending) that falls under this payroll cycle month will be displayed here.
                </div> */}
                {currentStep <= 1 && <ReusableTable config={configs[currentStep]} key={currentStep} />}
                {currentStep == 2 && <LOPReversal />}
            </div>
        </div>

    );
};

export default LeaveAppliedComponent;
